import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  fetchAdminProjectClaimReview,
  reviewAdminProjectClaim,
} from '../token/tokenIntelligenceApi';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 48px',
    maxWidth: 920,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: '14px 12px 32px' },
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#9ea4d8',
    fontSize: 13,
    textDecoration: 'none',
    marginBottom: 18,
  },
  panel: {
    padding: '24px 26px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.16), rgba(10,14,30,0.96))',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#e5e7ff',
    marginBottom: 8,
  },
  section: {
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
  },
  sectionLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    borderRadius: 999,
    border: '1px solid rgba(77,121,255,0.3)',
    background: 'rgba(77,121,255,0.12)',
    color: '#d5dcff',
    fontSize: 12,
    padding: '4px 10px',
  },
  textField: {
    marginTop: 14,
    '& .MuiOutlinedInput-input': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
    '& .MuiInputLabel-root': { color: '#9ea4d8' },
    '& .MuiFormHelperText-root': { color: '#8d93c9' },
  },
  actions: {
    display: 'flex',
    gap: 10,
    marginTop: 16,
  },
  primaryButton: {
    background: '#4d79ff',
    color: '#fff',
    fontWeight: 700,
  },
  secondaryButton: {
    color: '#cfd6ff',
    borderColor: 'rgba(255,255,255,0.24)',
  },
  muted: {
    color: '#9ea4d8',
    fontSize: 13,
    lineHeight: 1.55,
  },
  body: {
    color: '#d5dcff',
    fontSize: 13,
    lineHeight: 1.55,
  },
  success: {
    color: '#76ffb8',
    fontSize: 13,
    marginTop: 12,
  },
  error: {
    color: '#ff9a9a',
    fontSize: 13,
    marginTop: 12,
  },
}));

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function ProjectClaimReviewPage() {
  const classes = useStyles();
  const { claimId } = useParams();
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState('manual_review');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    fetchAdminProjectClaimReview(claimId)
      .then((result) => {
        if (!active) return;
        setData(result);
      })
      .catch((err) => {
        if (!active) return;
        setError(err && err.message ? err.message : 'Failed to load claim review');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [claimId]);

  const handleSubmit = async () => {
    setSaving(true);
    setStatus('');
    setError('');
    try {
      const result = await reviewAdminProjectClaim(claimId, { decision, reason });
      setData(result);
      setStatus(`Review saved as ${decision}.`);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const claim = data && data.claim ? data.claim : null;
  const identity = data && data.identity ? data.identity : null;

  return (
    <Box className={classes.root}>
      <RouterLink to={claim && claim.tokenAddress ? `/token/${claim.tokenAddress}` : '/token'} className={classes.backLink}>
        ← Back to token
      </RouterLink>

      <Box className={classes.panel}>
        <Typography className={classes.title}>Project Claim Review</Typography>
        <Typography className={classes.muted}>
          Level 5 manual-review control for project-claim sessions.
        </Typography>

        {loading ? <Typography className={classes.body} style={{ marginTop: 16 }}>Loading claim review…</Typography> : null}
        {!loading && error ? <Typography className={classes.error}>{error}</Typography> : null}

        {!loading && claim ? (
          <>
            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Claim</Typography>
              <Box className={classes.row}>
                <Box className={classes.badge}>{claim.status}</Box>
                <Box className={classes.badge}>Level {claim.verificationLevel}</Box>
                <Box className={classes.badge}>{claim.tokenChain}</Box>
                {identity && identity.reputationTier ? <Box className={classes.badge}>Tier {identity.reputationTier}</Box> : null}
              </Box>
              <Typography className={classes.body} style={{ marginTop: 10 }}>
                {claim.tokenName || claim.tokenSymbol ? `${claim.tokenName || ''}${claim.tokenSymbol ? ` (${claim.tokenSymbol})` : ''}` : 'Unnamed token'}
              </Typography>
              <Typography className={classes.muted}>{claim.tokenAddress}</Typography>
              {claim.requestedByWallet ? (
                <Typography className={classes.muted}>Requested by: {claim.requestedByWallet}</Typography>
              ) : null}
              {claim.createdAt ? <Typography className={classes.muted}>Created: {formatDate(claim.createdAt)}</Typography> : null}
              {claim.fraudFlags && claim.fraudFlags.length ? (
                <Typography className={classes.error}>Fraud flags: {claim.fraudFlags.join(', ')}</Typography>
              ) : null}
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Identity Snapshot</Typography>
              <Typography className={classes.body}>
                Reputation tier: <strong>{identity && identity.reputationTier ? identity.reputationTier : 'explorer'}</strong>
              </Typography>
              <Typography className={classes.body}>
                Completeness: <strong>{identity && identity.completenessScore != null ? identity.completenessScore : 0}</strong>
              </Typography>
              <Typography className={classes.body}>
                Badges: <strong>{identity && identity.earnedBadges && identity.earnedBadges.length ? identity.earnedBadges.join(', ') : 'none'}</strong>
              </Typography>
              {identity && identity.activeClaim && identity.activeClaim.claimantWallet ? (
                <Typography className={classes.muted}>Active claim wallet: {identity.activeClaim.claimantWallet}</Typography>
              ) : null}
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Review Decision</Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Decision"
                value={decision}
                onChange={(event) => setDecision(event.target.value)}
                className={classes.textField}
              >
                <MenuItem value="manual_review">Send to manual review</MenuItem>
                <MenuItem value="approve">Approve as Level 5</MenuItem>
                <MenuItem value="reject">Reject claim</MenuItem>
              </TextField>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label="Reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className={classes.textField}
                helperText="Stored on the manual-review decision record."
              />
              <Box className={classes.actions}>
                <Button className={classes.secondaryButton} variant="outlined" onClick={() => window.location.reload()} disabled={saving}>
                  Refresh
                </Button>
                <Button className={classes.primaryButton} variant="contained" onClick={handleSubmit} disabled={saving}>
                  Save Review
                </Button>
              </Box>
              {status ? <Typography className={classes.success}>{status}</Typography> : null}
              {error && !loading ? <Typography className={classes.error}>{error}</Typography> : null}
            </Box>

            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>Recent Activity</Typography>
              {(data.attempts || []).map((attempt) => (
                <Typography key={attempt.attemptId || `${attempt.claimId}:${attempt.createdAt}`} className={classes.muted}>
                  {formatDate(attempt.completedAt || attempt.createdAt)} · {attempt.method} · {attempt.status} · L{attempt.verificationLevel || 0}
                </Typography>
              ))}
              {(data.decisions || []).map((item) => (
                <Typography key={item.decisionId || `${item.subjectId}:${item.createdAt}`} className={classes.muted}>
                  {formatDate(item.createdAt)} · review {item.decision}{item.reason ? ` · ${item.reason}` : ''}
                </Typography>
              ))}
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
